import { useContext, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import { Avatar, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import type { InferType } from "yup";

import type {
  AddFavouriteRecipientRequest,
  CreateWorkItemAction,
  FormContextData,
  UUID,
} from "@thalamos/aspire-common";
import { recordOfDetentionTemplateIds } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import type {
  FormikInitialValues,
  FormikNewFormAssigneeValues,
  newFormAssigneeData,
} from "../../../components/FormAssignee/formAssigneeTypes.js";
import { routeFns } from "../../../routePaths.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { stringAvatar } from "~/components/layout/menu/helpers/index.js";
import { LoggedInUserContext, ManagedLocationsContext } from "~/Contexts.js";
import type { EmailRedirectEntry } from "~/Contexts.js";
import { assertOkResponse, isRefusal } from "~/helpers/assertOkResponse.js";
import {
  addFavouriteRecipient,
  removeFavouriteRecipient,
} from "~/helpers/favouriteRecipients.js";
import { logActionToDatadog, logErrorToDatadog } from "~/tracing.js";

export function mapGuestAccreditation(
  formContext: Omit<FormContextData, "patient">,
  value: string,
) {
  switch (value) {
    case "registered-medical-practitioner":
      return "medical-recommendation";
    case "approved-mental-health-professional":
      return "application";
    case "nurse-receiving-officer":
      return "record-of-detention";
    case "mental-health-act-administrator":
      if (
        (formContext.type === "admission" &&
          formContext.forms.find(
            (f) =>
              f.status === "complete" &&
              recordOfDetentionTemplateIds.includes(f.formTemplate.id),
          )) ||
        formContext.type === "police-incident"
      ) {
        return "finalise";
      } else {
        return "record-of-detention";
      }
    case "complete":
      return "complete";
    default:
      return "other";
  }
}

export function userNotSelectedFn(formikValues: FormikNewFormAssigneeValues) {
  return formikValues.values.type === "full-user"
    ? !formikValues.values.teamId
    : !formikValues.values.name ||
        !formikValues.values.guestInviteAccreditation;
}

export function shareToNonTrainingUserFromTrainingFn(
  formikValues: FormikNewFormAssigneeValues,
  userNotSelected: boolean,
  isTrainingContext: boolean,
) {
  return (
    !userNotSelected &&
    isTrainingContext &&
    !formikValues.values.isTrainingOrganisation
  );
}

export function shareToTrainingUserFromNonTrainingFn(
  formikValues: FormikNewFormAssigneeValues,
  userNotSelected: boolean,
  isTrainingContext: boolean,
) {
  return (
    !userNotSelected &&
    !isTrainingContext &&
    formikValues.values.isTrainingOrganisation
  );
}

/**
 * Returns true if the selected recipient is in a different data controller
 * than the current user (excluding guest and independent recipient contexts).
 *
 * Note: When the *sender* is in a guest, soad, or independent context the
 * entire data-controller warning feature is disabled at the call-site, so
 * this function will not be reached in those cases.
 *
 * When `dataSharingPartnerships` is provided, recipients whose data controller
 * shares a partnership with the user's data controller are NOT considered outside.
 */
export function isShareOutsideDataControllerFn(
  formikValues: FormikNewFormAssigneeValues,
  userNotSelected: boolean,
  userDataControllerId: string | null | undefined,
  dataSharingPartnerships: { dataControllers: { id: string }[] }[] = [],
): boolean {
  if (userNotSelected) return false;

  const recipientTeamType = formikValues.values.teamType;
  const recipientDataControllerId = formikValues.values.dataControllerId;

  // Don't show warning for guest or independent contexts
  if (recipientTeamType === "guest" || recipientTeamType === "independent") {
    return false;
  }

  // If user has no data controller, don't show warning
  if (!userDataControllerId) return false;

  // If recipient has no data controller, don't show warning (handles edge cases)
  if (!recipientDataControllerId) return false;

  // Same data controller — not outside
  if (userDataControllerId === recipientDataControllerId) return false;

  // Check if the two data controllers share a data sharing partnership
  const inSamePartnership = dataSharingPartnerships.some(
    (partnership) =>
      partnership.dataControllers.some(
        (dc) => dc.id === userDataControllerId,
      ) &&
      partnership.dataControllers.some(
        (dc) => dc.id === recipientDataControllerId,
      ),
  );
  if (inSamePartnership) return false;

  // Show warning if data controllers are different and not partnered
  return true;
}

/**
 * Returns true if the selected recipient is in an independent context.
 */
export function isShareToIndependentFn(
  formikValues: FormikNewFormAssigneeValues,
  userNotSelected: boolean,
): boolean {
  if (userNotSelected) return false;

  return formikValues.values.teamType === "independent";
}

export function SendRequestButton({
  shareToNonTrainingUserFromTraining,
  shareToTrainingUserFromNonTraining,
  recipients,
  setSubmitError,
  formContextId,
  reloadFormContext,
  patientId,
  navigate,
  signForm,
  canAddRecipient,
  isSoadFormContext,
  formType,
  openS12PaymentRequest,
  s12OutcomeType,
  s12FormId,
  selectedInterviewId,
}: {
  shareToNonTrainingUserFromTraining: boolean;
  shareToTrainingUserFromNonTraining: boolean | undefined;
  recipients: InferType<typeof newFormAssigneeData>[];
  setSubmitError: (param: any) => void;
  formContextId: UUID;
  reloadFormContext: () => void;
  patientId: string;
  navigate: (param: any, options: any) => void;
  signForm?: () => void;
  canAddRecipient: boolean | undefined;
  isSoadFormContext: boolean;
  formType: FormContextData["type"];
  /** If true, after sending, redirect to S12 payment request dialog */
  openS12PaymentRequest?: boolean;
  /** Outcome type for S12 payment request */
  s12OutcomeType?: string;
  /** Form ID for S12 payment request */
  s12FormId?: string;
  /** Interview ID to link recipients to after send (drFinder flow) */
  selectedInterviewId?: string | null;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A failure part-way through the recipients leaves Send enabled with all of
  // them still listed, so a retry must skip those already sent or create their
  // work items twice. Matched by identity: the list must keep each recipient's
  // object for as long as it is listed.
  const sentRecipients = useRef(
    new WeakSet<InferType<typeof newFormAssigneeData>>(),
  );
  const { emailRedirects } = useContext(ManagedLocationsContext);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
        mt: 2,
      }}
    >
      <Button
        testId="send-button"
        label={"send"}
        disabled={
          isSubmitting ||
          shareToNonTrainingUserFromTraining ||
          shareToTrainingUserFromNonTraining ||
          recipients.length === 0 ||
          canAddRecipient
        }
        onClick={async () => {
          setSubmitError(null);
          setIsSubmitting(true);
          let hasError = false;
          if (signForm) await signForm();

          // TODO - think about changing this to a batch request or something like that?
          for (const recipient of recipients) {
            if (sentRecipients.current.has(recipient)) continue;
            const workItemId = v4();
            const result = await api.work
              .create(workItemId, {
                formContextId: formContextId,
                ...(recipient.type === "full-user"
                  ? {
                      teamId: recipient.teamId!,
                      assignedUserId: recipient.assignedUserId,
                      type: "full-user",
                    }
                  : {
                      type: "guest",
                      email: recipient.email!,
                      name: recipient.name!,
                    }),
                action: recipient.action as CreateWorkItemAction | undefined,
              })
              // Axios rejects a status outside this request's validateStatus;
              // report it like one it resolves.
              .catch((error: unknown) =>
                isAxiosError(error) ? error.response : undefined,
              );

            if (!result || result.status < 200 || result.status >= 300) {
              setIsSubmitting(false);
              hasError = true;
              const errorMessage = result?.data?.reason || "Unknown error";
              renderErrorToast({ message: errorMessage });
              setSubmitError(errorMessage);
              logErrorToDatadog(errorMessage);
              break;
            }
            sentRecipients.current.add(recipient);

            // Log the action to datadog only if the recipient is a guest
            if (recipient.type === "guest") {
              logActionToDatadog("Work item created", {
                workItemId: workItemId,
                formType,
                formContextId: formContextId,
                type: recipient.guestInviteAccreditation,
                action: recipient.action,
              });
            }
          }

          if (!hasError) {
            const recipientNames = recipients.map((r) => {
              const redirect = emailRedirects.find(
                (er) => er.sourceEmail.toLowerCase() === r.email?.toLowerCase(),
              );
              return redirect ? (redirect.targetTeamName ?? r.name) : r.name;
            });
            const message = `${isSoadFormContext ? "Assessment" : "Invites"} successfully sent to ${recipientNames.join(", ")}`;
            renderSuccessToast({ message });

            // Link recipients to the selected interview (drFinder flow)
            if (selectedInterviewId) {
              for (const recipient of recipients) {
                const userId = recipient.assignedUserId;
                if (recipient.type !== "full-user" || !userId) continue;
                try {
                  assertOkResponse(
                    await api.interviews.addParticipant(selectedInterviewId, {
                      userId,
                      teamId: recipient.teamId ?? undefined,
                      status: "pending",
                      inviteSource: "known",
                      contactMethod: ["email"],
                    }),
                  );
                } catch (error) {
                  // The work item is already sent, so this is reported
                  // without failing the send.
                  const notAdded = `${recipient.name} could not be added to the interview`;
                  renderErrorToast({
                    message: isRefusal(error)
                      ? `${notAdded}: ${error.message}`
                      : `${notAdded}.`,
                  });
                }
              }
            }

            reloadFormContext();

            // If S12 payment request should be opened after sending, redirect there
            if (openS12PaymentRequest && s12OutcomeType) {
              navigate(
                routeFns.formContextS12PaymentRequest(
                  formContextId,
                  patientId,
                  {
                    outcomeType: s12OutcomeType,
                    formId: s12FormId,
                  },
                ),
                { replace: true },
              );
            } else {
              navigate(
                routeFns.formContextPageSuccessDialog(
                  formContextId,
                  patientId,
                  {
                    title: `${isSoadFormContext ? "Assessment" : "Invites"} successfully sent`,
                    message: message,
                  },
                ),
                { replace: true },
              );
            }
            setIsSubmitting(false);
          }
        }}
      />
    </Box>
  );
}

export function AddRecipientButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean | undefined;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
        mt: 2,
      }}
    >
      <Button
        testId="add-recipient-button"
        label={"add recipient"}
        disabled={disabled}
        onClick={onClick}
      />
    </Box>
  );
}

export function RecipientsList({
  recipients,
  isMobileView,
  setRecipients,
  setSubmitError,
  emailRedirects,
}: {
  recipients: InferType<typeof newFormAssigneeData>[];
  isMobileView: boolean;
  setRecipients: (param: any) => void;
  setSubmitError: (param: any) => void;
  emailRedirects?: { sourceEmail: string; targetTeamName: string | null }[];
}) {
  const { t } = useTranslation();
  const userContext = useContext(LoggedInUserContext);
  const favouriteRecipients = userContext?.user.favouriteRecipients ?? [];
  const sessionDataControllerId =
    userContext?.user.sessionContext?.dataControllerId;
  const sessionDataControllerName =
    userContext?.user.sessionContext?.dataControllerName;

  const isFavourite = (
    recipient: InferType<typeof newFormAssigneeData>,
  ): boolean => {
    if (!sessionDataControllerId) return false;
    return favouriteRecipients.some(
      (fav) =>
        fav.dataControllerId === sessionDataControllerId &&
        fav.teamId === recipient.teamId &&
        (recipient.assignedUserId
          ? fav.userId === recipient.assignedUserId
          : !fav.userId),
    );
  };

  const handleToggleFavourite = async (
    recipient: InferType<typeof newFormAssigneeData>,
  ): Promise<void> => {
    if (!userContext || !sessionDataControllerId || !sessionDataControllerName)
      return;

    const isCurrentlyFavourite = isFavourite(recipient);

    if (isCurrentlyFavourite) {
      await removeFavouriteRecipient(
        sessionDataControllerId,
        recipient.teamId!,
        recipient.assignedUserId,
        userContext.refetchUser,
      );
    } else {
      const favourite: AddFavouriteRecipientRequest = {
        dataControllerId: sessionDataControllerId,
        dataControllerName: sessionDataControllerName,
        teamId: recipient.teamId!,
        ...(recipient.assignedUserId && {
          userId: recipient.assignedUserId,
        }),
      };
      await addFavouriteRecipient(favourite, userContext.refetchUser);
    }
  };

  return (
    <Box
      data-testid="recipient-chips-container"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        marginTop: 2,
        justifyContent: isMobileView ? "center" : "flex-start",
      }}
    >
      {recipients.map((recipient, index) => {
        const showFavouriteStar =
          recipient.type === "full-user" && recipient.teamId;

        const redirect = emailRedirects?.find(
          (er) =>
            er.sourceEmail.toLowerCase() === recipient.email?.toLowerCase(),
        );

        return (
          <Box
            data-testid="recipient-chip"
            key={index}
            sx={{
              display: "flex",
              marginRight: 2,
              marginBottom: 2,
              alignItems: "center",
              border: "1px solid black",
              width: isMobileView ? "100%" : "auto",
              justifyContent: isMobileView ? "center" : "flex-start",
              padding: 1.5,
            }}
          >
            {showFavouriteStar && (
              <Tooltip
                title={
                  isFavourite(recipient)
                    ? t(
                        "pages.formDraftSignPage.favourites.removeFavouriteTooltip",
                      )
                    : t(
                        "pages.formDraftSignPage.favourites.addFavouriteTooltip",
                      )
                }
                placement="top"
              >
                <IconButton
                  aria-label={
                    isFavourite(recipient)
                      ? t("pages.formDraftSignPage.favourites.removeFavourite")
                      : t("pages.formDraftSignPage.favourites.addFavourite")
                  }
                  size="medium"
                  onClick={() => handleToggleFavourite(recipient)}
                  sx={{ mr: 1 }}
                >
                  {isFavourite(recipient) ? (
                    <Star color="warning" sx={{ fontSize: 28 }} />
                  ) : (
                    <StarBorder sx={{ fontSize: 28 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Avatar
              sx={{
                backgroundColor:
                  recipient.type === "guest" ? "" : "primary.main",
                marginRight: 2,
              }}
              {...stringAvatar(recipient.name)}
            />
            <Box>
              <Typography>
                {redirect
                  ? (redirect.targetTeamName ?? recipient.name)
                  : recipient.name}
                {!redirect &&
                  (recipient.teamType === "independent"
                    ? ` (${recipient.teamType})`
                    : recipient.teamName
                      ? ` (${recipient.teamName})`
                      : recipient.type === "guest"
                        ? ` (${recipient.type})`
                        : "")}
                {redirect && " (redirected)"}
              </Typography>
              <Typography>
                {redirect
                  ? `Redirected from ${recipient.email}`
                  : recipient.email}
              </Typography>
            </Box>
            <IconButton
              aria-label={"Close"}
              size="small"
              onClick={() => {
                setSubmitError(null);
                setRecipients(
                  recipients.filter((_: unknown, i: number) => i !== index),
                );
              }}
              sx={{
                marginLeft: "auto",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}

export function handleAddRecipient({
  formikValues,
  setRecipients,
  initialValues,
  action,
  emailRedirects,
}: {
  formikValues: FormikNewFormAssigneeValues;
  setRecipients: (param: any) => void;
  initialValues: FormikInitialValues;
  action: CreateWorkItemAction | undefined;
  emailRedirects?: EmailRedirectEntry[];
}) {
  const teamMember = formikValues.values.userMembershipContext.find(
    (m) => m.role !== "manager" && formikValues.values.teamId === m.id,
  );

  // When a guest email is being added but the email has been redirected to another team,
  // treat it as a full-user invite to the target team instead. This avoids sending the
  // redirected address to the server and gives the target team a proper work item.
  const isGuestType = formikValues.values.type === "guest";
  const guestEmail = formikValues.values.email;
  const guestEmailLower = guestEmail?.toLowerCase();
  const redirect =
    isGuestType && guestEmailLower
      ? emailRedirects?.find(
          (er) => er.sourceEmail.toLowerCase() === guestEmailLower,
        )
      : undefined;

  const newRecipient = redirect
    ? {
        type: "full-user" as const,
        teamId: redirect.targetTeamId,
        name: redirect.targetTeamName ?? formikValues.values.name,
        teamName: redirect.targetTeamName ?? undefined,
        // Preserve the original guest email so downstream code that accesses
        // recipient.email (e.g. redirect label rendering) continues to work.
        // The server API payload for full-user recipients does not include email,
        // so this value is display-only.
        email: guestEmail,
        // Clear the requestType/action for redirected recipients: the
        // accreditation selector is only shown for guest types, so a redirected
        // guest should behave exactly like a plain full-user invite with no
        // pre-selected action.
        action: undefined,
        mentalHealthCareProviderId:
          formikValues.values.mentalHealthCareProviderId,
      }
    : {
        ...(formikValues.values.type === "full-user"
          ? {
              teamId: formikValues.values.teamId!,
              assignedUserId: formikValues.values.assignedUserId,
              type: "full-user",
              email: formikValues.values.email,
              name: formikValues.values.name,
              teamName: teamMember?.name,
              teamType: formikValues.values.teamType,
              dataControllerId: formikValues.values.dataControllerId,
              dataControllerName: formikValues.values.dataControllerName,
            }
          : {
              type: "guest",
              email: formikValues.values.email!,
              name: formikValues.values.name!,
              guestInviteAccreditation:
                formikValues.values.guestInviteAccreditation,
            }),
        action: action ?? undefined,
        mentalHealthCareProviderId:
          formikValues.values.mentalHealthCareProviderId,
      };

  setRecipients((prevRecipients: InferType<typeof newFormAssigneeData>[]) => {
    // Filter out any existing recipient for the same team (whether team-level or user-specific).
    // Only one work item per team is allowed - the last one added takes priority.
    const filteredRecipients = prevRecipients.filter((existing) => {
      if (redirect) {
        // Adding a redirected guest as a full-user recipient: remove any existing
        // recipient for the same target team, or any guest with the same source email.
        if (existing.type === "full-user") {
          return existing.teamId !== redirect.targetTeamId;
        }
        if (existing.type === "guest") {
          return existing.email?.toLowerCase() !== guestEmailLower;
        }
        return true;
      }
      if (
        formikValues.values.type === "full-user" &&
        existing.type === "full-user"
      ) {
        // Remove any existing recipient from the same team
        return existing.teamId !== formikValues.values.teamId;
      }
      if (formikValues.values.type === "guest" && existing.type === "guest") {
        // For guest, filter out same email
        return existing.email !== formikValues.values.email;
      }
      return true;
    });

    return [...filteredRecipients, newRecipient];
  });

  formikValues.setValues(initialValues);
  formikValues.setTouched({ email: false });
}

export function TrainingWarningBanners({
  shareToNonTrainingUserFromTraining,
  shareToTrainingUserFromNonTraining,
}: {
  shareToNonTrainingUserFromTraining: boolean;
  shareToTrainingUserFromNonTraining: boolean | undefined;
}) {
  const { t } = useTranslation();

  return (
    <>
      {shareToNonTrainingUserFromTraining && (
        <Box sx={{ mb: 4 }}>
          <Banner
            title={t("training.onlyShareToTrainingUsers")}
            bannerType={BannerList.WARNING}
          />
        </Box>
      )}
      {shareToTrainingUserFromNonTraining && (
        <Box sx={{ mb: 4 }}>
          <Banner
            title={t("training.cannotShareToTrainingUsers")}
            bannerType={BannerList.WARNING}
          />
        </Box>
      )}
    </>
  );
}
